// src/features/admin/AmenityBulkSetPanel.jsx
//
// Admin panel: bulk-apply a single amenity to multiple locations.
//
// Purpose: Mother's Room and Parking (and any future amenities) were added
// to the catalog and edit modal, but existing locations need their
// location_amenities rows updated. This panel lets an admin select an
// amenity, toggle present on/off, choose a subset of locations, and apply
// the change in one call instead of editing each location individually.
//
// Layout:
//   1. Amenity picker — select from AMENITY_CATALOG
//   2. Present toggle — "Present on-site" | "Not present"
//   3. Location checklist — fetched from GET /api/locations
//      (search filter + Select all / Clear all)
//   4. Apply button + result banner
//
// All colors via T tokens; no raw hex.

import React, { useEffect, useState, useMemo } from "react";
import { T } from "../../app/tokens.js";
import { AMENITY_CATALOG } from "../../data/amenityCatalog.js";
import { postBulkSetAmenity } from "../../data/amenitiesClient.js";
import { log } from "../../utils/logger.js";
import { rankSites } from "../../utils/rankSites.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const LOCATIONS_API = "/api/locations";

// Stages that represent an "active presence" — the only ones eligible for bulk
// amenity updates.  Prospecting / negotiating / dormant / closed are excluded.
const ELIGIBLE_STAGES = new Set(["operational", "build", "vacated"]);

// Hub chip colours — routed through T tokens (hubChipBg / hubChipBorder / hubChipText).
const HUB_CHIP = {
  bg:     T.hubChipBg,
  border: T.hubChipBorder,
  text:   T.hubChipText,
};

// ─── Inline styles ────────────────────────────────────────────────────────────

const SECTION_TITLE = {
  margin: "0 0 4px 0",
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  letterSpacing: -0.2,
};

const SECTION_SUBTITLE = {
  fontSize: T.fsBase,
  color: T.mid,
  marginBottom: 20,
  lineHeight: 1.5,
};

const LABEL = {
  display: "block",
  fontSize: T.fsXxs,
  fontWeight: T.fwSemibold,
  color: T.mid,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: 6,
};

const SELECT_STYLE = {
  width: "100%",
  padding: "8px 10px",
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: T.fsBase,
  color: T.dark,
  background: T.white,
  outline: "none",
  cursor: "pointer",
};

// ─── PresenceToggle ───────────────────────────────────────────────────────────

function PresenceToggle({ value, onChange }) {
  const buttonStyle = (active) => ({
    padding: "7px 18px",
    border: `1px solid ${active ? T.blue : T.border}`,
    background: active ? T.blue : T.white,
    color: active ? T.white : T.mid,
    borderRadius: 5,
    cursor: "pointer",
    fontSize: T.fsBase,
    fontWeight: active ? T.fwSemibold : T.fwNormal,
    outline: "none",
  });

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <button
        type="button"
        data-testid="presence-toggle-present"
        style={buttonStyle(value === true)}
        onClick={() => onChange(true)}
      >
        Present on-site
      </button>
      <button
        type="button"
        data-testid="presence-toggle-absent"
        style={buttonStyle(value === false)}
        onClick={() => onChange(false)}
      >
        Not present
      </button>
    </div>
  );
}

// ─── LocationChecklist ────────────────────────────────────────────────────────

function LocationChecklist({ locations, selected, onToggle, onSelectAll, onClearAll }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return locations;
    return locations.filter(
      (loc) =>
        matchesFoldedQuery(q, loc.id, loc.market, loc.locationAlias)
    );
  }, [locations, search]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <input
          type="text"
          placeholder="Filter locations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="location-search"
          style={{
            flex: 1,
            padding: "7px 10px",
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            fontSize: T.fsBase,
            color: T.dark,
            outline: "none",
          }}
        />
        <button
          type="button"
          data-testid="select-all-btn"
          onClick={() => onSelectAll(filtered.map((l) => l.id))}
          style={{
            padding: "7px 12px",
            border: `1px solid ${T.border}`,
            borderRadius: 5,
            background: T.white,
            color: T.mid,
            fontSize: T.fsXs,
            cursor: "pointer",
            fontWeight: T.fwSemibold,
            whiteSpace: "nowrap",
          }}
        >
          Select all ({filtered.length})
        </button>
        <button
          type="button"
          data-testid="clear-all-btn"
          onClick={onClearAll}
          style={{
            padding: "7px 12px",
            border: `1px solid ${T.border}`,
            borderRadius: 5,
            background: T.white,
            color: T.mid,
            fontSize: T.fsXs,
            cursor: "pointer",
            fontWeight: T.fwSemibold,
          }}
        >
          Clear
        </button>
      </div>

      <div
        data-testid="location-checklist"
        style={{
          maxHeight: 320,
          overflowY: "auto",
          border: `1px solid ${T.border}`,
          borderRadius: 6,
        }}
      >
        {filtered.length === 0 && (
          <div style={{ padding: "14px 12px", color: T.light, fontSize: T.fsBase }}>
            No locations match your filter.
          </div>
        )}
        {filtered.map((loc, idx) => {
          const isChecked = selected.has(loc.id);
          const primaryLabel = loc.market
            ? (loc.locationAlias ? `${loc.market} · ${loc.locationAlias}` : loc.market)
            : loc.id;
          return (
            <label
              key={loc.id}
              data-testid={`loc-row-${loc.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                borderTop: idx === 0 ? "none" : `1px solid ${T.hair}`,
                cursor: "pointer",
                background: isChecked ? T.tilePale : "transparent",
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggle(loc.id)}
                data-testid={`loc-checkbox-${loc.id}`}
                style={{ flexShrink: 0, cursor: "pointer" }}
              />
              <span style={{ fontWeight: T.fwSemibold, color: T.dark, fontSize: T.fsBase }}>
                {primaryLabel}
              </span>
              <span style={{ color: T.mid, fontSize: T.fsXs, flexShrink: 0 }}>
                {loc.id}
              </span>
              {loc.isHub && (
                <span
                  data-testid={`loc-hub-chip-${loc.id}`}
                  style={{
                    display:      "inline-flex",
                    alignItems:   "center",
                    gap:          3,
                    background:   HUB_CHIP.bg,
                    border:       `1px solid ${HUB_CHIP.border}`,
                    color:        HUB_CHIP.text,
                    fontSize:     T.fsXxs,
                    fontWeight:   T.fwBold,
                    padding:      "1px 5px",
                    borderRadius: 4,
                    flexShrink:   0,
                  }}
                >
                  ◆ Hub
                </span>
              )}
            </label>
          );
        })}
      </div>
      <div style={{ marginTop: 6, fontSize: T.fsXs, color: T.light }}>
        {selected.size} of {locations.length} location{locations.length !== 1 ? "s" : ""} selected
      </div>
    </div>
  );
}

// ─── ResultBanner ─────────────────────────────────────────────────────────────

function ResultBanner({ result, error }) {
  if (error) {
    return (
      <div
        role="alert"
        data-testid="bulk-set-error"
        style={{
          padding: "10px 14px",
          background: T.redSoft,
          border: `1px solid ${T.redLight}`,
          borderRadius: 6,
          color: T.red,
          fontSize: T.fsBase,
          marginTop: 16,
        }}
      >
        {error}
      </div>
    );
  }
  if (!result) return null;
  return (
    <div
      role="status"
      data-testid="bulk-set-result"
      style={{
        padding: "10px 14px",
        background: T.greenSoft,
        border: `1px solid ${T.greenLight}`,
        borderRadius: 6,
        color: T.greenDark,
        fontSize: T.fsBase,
        marginTop: 16,
      }}
    >
      Done - {result.updated} location{result.updated !== 1 ? "s" : ""} updated
      {result.skipped > 0 && ` (${result.skipped} skipped - not found or deleted)`}.
    </div>
  );
}

// ─── AmenityBulkSetPanel ──────────────────────────────────────────────────────

export function AmenityBulkSetPanel() {
  const [locations,    setLocations]    = useState(null);   // null = loading
  const [loadError,    setLoadError]    = useState(null);
  const [amenityKey,   setAmenityKey]   = useState(AMENITY_CATALOG[0]?.key ?? "");
  const [present,      setPresent]      = useState(true);
  const [selected,     setSelected]     = useState(new Set());
  const [saving,       setSaving]       = useState(false);
  const [saveResult,   setSaveResult]   = useState(null);   // { updated, skipped }
  const [saveError,    setSaveError]    = useState(null);

  // Fetch all locations once on mount
  useEffect(() => {
    let cancelled = false;
    fetch(LOCATIONS_API)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load locations (${r.status})`);
        return r.json();
      })
      .then((data) => {
        if (!cancelled) {
          const list = Array.isArray(data) ? data : (data.locations ?? []);
          const eligible = list.filter(
            (l) => !l.id?.startsWith("TEST-") && ELIGIBLE_STAGES.has(l.stage)
          );
          setLocations(rankSites(eligible));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.error("[AmenityBulkSetPanel] failed to load locations", { err: String(err) });
          setLoadError("Could not load locations. Please refresh and try again.");
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Reset result when the amenity/present selection changes
  function handleAmenityChange(key) {
    setAmenityKey(key);
    setSaveResult(null);
    setSaveError(null);
  }

  function handleToggle(locId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId);
      else next.add(locId);
      return next;
    });
    setSaveResult(null);
    setSaveError(null);
  }

  function handleSelectAll(ids) {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    setSaveResult(null);
    setSaveError(null);
  }

  function handleClearAll() {
    setSelected(new Set());
    setSaveResult(null);
    setSaveError(null);
  }

  async function handleApply() {
    if (saving || selected.size === 0) return;
    setSaving(true);
    setSaveResult(null);
    setSaveError(null);
    try {
      const result = await postBulkSetAmenity({
        amenityKey,
        present,
        locationIds: Array.from(selected),
      });
      setSaveResult(result);
      setSelected(new Set());
    } catch (err) {
      log.error("[AmenityBulkSetPanel] bulk-set failed", { err: String(err) });
      setSaveError(err?.message || "Bulk update failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // ── Loading / error state ─────────────────────────────────────────────────
  if (loadError) {
    return (
      <div>
        <h2 style={SECTION_TITLE}>Amenity Bulk Set</h2>
        <div
          role="alert"
          data-testid="load-error"
          style={{ color: T.red, fontSize: T.fsBase, marginTop: 12 }}
        >
          {loadError}
        </div>
      </div>
    );
  }

  if (locations === null) {
    return (
      <div>
        <h2 style={SECTION_TITLE}>Amenity Bulk Set</h2>
        <div
          data-testid="bulk-set-loading"
          style={{ color: T.light, fontSize: T.fsBase, marginTop: 12 }}
        >
          Loading locations…
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  const selectedEntry = AMENITY_CATALOG.find((e) => e.key === amenityKey);

  return (
    <div data-testid="amenity-bulk-set-panel">
      <h2 style={SECTION_TITLE}>Amenity Bulk Set</h2>
      <p style={SECTION_SUBTITLE}>
        Apply a single amenity change to multiple locations at once.
        Useful for marking newly-catalogued amenities (e.g. Mother&apos;s Room,
        Parking) across all locations that have them, without editing each location
        individually.
      </p>

      {/* ── Step 1: Choose amenity ───────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <label htmlFor="amenity-key-select" style={LABEL}>
          Amenity
        </label>
        <select
          id="amenity-key-select"
          data-testid="amenity-key-select"
          value={amenityKey}
          onChange={(e) => handleAmenityChange(e.target.value)}
          style={SELECT_STYLE}
        >
          {AMENITY_CATALOG.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>
      </div>

      {/* ── Step 2: Present / not present ───────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={LABEL}>Set to</div>
        <PresenceToggle value={present} onChange={(v) => { setPresent(v); setSaveResult(null); setSaveError(null); }} />
      </div>

      {/* ── Step 3: Location checklist ───────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={LABEL}>Locations to update</div>
        <LocationChecklist
          locations={locations}
          selected={selected}
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
        />
      </div>

      {/* ── Step 4: Apply ────────────────────────────────────────────────── */}
      <button
        type="button"
        data-testid="bulk-apply-btn"
        disabled={selected.size === 0 || saving}
        onClick={handleApply}
        style={{
          padding: "9px 22px",
          background: selected.size === 0 || saving ? T.border : T.blue,
          color: selected.size === 0 || saving ? T.mid : T.white,
          border: "none",
          borderRadius: 6,
          fontSize: T.fsBase,
          fontWeight: T.fwSemibold,
          cursor: selected.size === 0 || saving ? "not-allowed" : "pointer",
          outline: "none",
        }}
      >
        {saving
          ? "Applying…"
          : `Apply to ${selected.size} location${selected.size !== 1 ? "s" : ""}`
        }
      </button>

      <ResultBanner result={saveResult} error={saveError} />
    </div>
  );
}
